<template>
  <div class="page-container">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <PageHeader title="Completed Payments" :subtitle="subtitle" icon="mdi-cash-check" :count="payments.length">
      <template v-slot:actions>
        <v-btn
          v-for="e in exportButtons"
          :key="e.kind"
          depressed
          class="btn-act"
          :class="e.tone"
          :loading="exporting === e.kind"
          :disabled="!payments.length || (!!exporting && exporting !== e.kind)"
          :title="e.title"
          @click="runExport(e.kind)"
        >
          <v-icon left small>{{ e.icon }}</v-icon>
          {{ e.label }}
        </v-btn>
      </template>
    </PageHeader>

    <!-- Totals for what the filters show -->
    <div class="pay-summary">
      <div class="pay-stat">
        <span class="pay-stat-label">Collected</span>
        <span class="pay-stat-value">₹{{ money(total) }}</span>
      </div>
      <div class="pay-stat">
        <span class="pay-stat-label">Payments</span>
        <span class="pay-stat-value">{{ payments.length }}</span>
      </div>
      <div class="pay-stat">
        <span class="pay-stat-label">Period</span>
        <span class="pay-stat-value pay-stat-value--sm">{{ periodLabel }}</span>
      </div>
    </div>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          id="pay-search"
          v-model="search"
          class="filter-bar-search"
          outlined
          dense
          hide-details
          clearable
          label="Name, application no., GRN, CIN or department ID"
          prepend-inner-icon="mdi-magnify"
          @input="searchChanged"
          @click:clear="clearSearch"
        ></v-text-field>
        <v-menu v-model="fromMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              id="pay-from"
              :value="formatDay(fromDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="From date"
              prepend-inner-icon="mdi-calendar-start"
              v-bind="attrs"
              v-on="on"
              @click:clear="fromDate = null; load()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fromDate" :max="toDate || today" color="#15803d" @change="fromMenu = false; load()"></v-date-picker>
        </v-menu>
        <v-menu v-model="toMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              id="pay-to"
              :value="formatDay(toDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="To date"
              prepend-inner-icon="mdi-calendar-end"
              v-bind="attrs"
              v-on="on"
              @click:clear="toDate = null; load()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="toDate" :min="fromDate" :max="today" color="#15803d" @change="toMenu = false; load()"></v-date-picker>
        </v-menu>
        <v-btn v-if="hasFilters" depressed class="btn-act btn-act--neutral filter-bar-button" @click="clearFilters">
          <v-icon left small>mdi-filter-remove-outline</v-icon>
          Clear filters
        </v-btn>
      </div>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!payments.length" class="empty-state">
        <v-icon size="52">mdi-cash-remove</v-icon>
        <div class="cell-strong">{{ hasFilters ? "No payments match these filters" : "No completed payments yet" }}</div>
        <div>{{ hasFilters ? "Change the search or dates, or clear the filters." : "Payments appear here once applicants pay through eTreasury." }}</div>
      </div>

      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th>Paid on</th>
              <th>Party</th>
              <th>Amount</th>
              <th>eTreasury reference</th>
              <th>Codes</th>
              <th class="text-right">Application</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in pageRows" :key="(p.APP_ID || p.GRN || '') + i">
              <td data-label="Paid on">
                <div>
                  <div class="cell-strong">{{ formatDay(p.PAYMENT_DATE) }}</div>
                  <div class="cell-muted">{{ formatTime(p.PAYMENT_DATE) }}</div>
                </div>
              </td>
              <td data-label="Party">
                <div>
                  <div class="cell-strong">{{ p.PARTY_NAME || "—" }}</div>
                  <div v-if="p.APPLICATION_NO" class="cell-muted">#{{ p.APPLICATION_NO }}</div>
                  <div v-if="p.PAN_NO" class="cell-muted">PAN {{ p.PAN_NO }}</div>
                </div>
              </td>
              <td data-label="Amount">
                <span class="pay-amount">₹{{ money(p.CHALLAN_AMOUNT) }}</span>
              </td>
              <td data-label="eTreasury reference">
                <div v-if="p.GRN || p.CIN || p.DEPARTMENT_ID" class="ref-list">
                  <div v-if="p.GRN"><span class="ref-key">GRN</span><span class="ref-val">{{ p.GRN }}</span></div>
                  <div v-if="p.CIN"><span class="ref-key">CIN</span><span class="ref-val">{{ p.CIN }}</span></div>
                  <div v-if="p.DEPARTMENT_ID"><span class="ref-key">Dept ID</span><span class="ref-val">{{ p.DEPARTMENT_ID }}</span></div>
                </div>
                <span v-else class="cell-muted">{{ filling && p.APP_ID ? "Looking up…" : "No eTreasury reference" }}</span>
              </td>
              <td data-label="Codes">
                <div class="ref-list">
                  <div><span class="ref-key">Office</span><span class="ref-val">{{ p.OFFICE_CODE || "—" }}</span></div>
                  <div><span class="ref-key">E-Treasury Office Code</span><span class="ref-val">{{ p.DIVISION_CODE || "—" }}</span></div>
                  <div v-if="p.HEADID"><span class="ref-key">Head</span><span class="ref-val">{{ p.HEADID }}</span></div>
                </div>
              </td>
              <td class="app-table-actions text-right">
                <div class="row-actions">
                  <v-btn v-if="p.APP_ID" depressed small class="btn-act btn-act--sm btn-act--primary" @click="openApplication(p)">
                    <v-icon left small>mdi-eye-outline</v-icon>
                    Open
                  </v-btn>
                  <span v-else class="cell-muted">—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="pages > 1" class="pagination-bar">
        <v-pagination v-model="page" :length="pages" :total-visible="7" color="#15803d"></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";
import { getUserType } from "@/components/Common/navConfig";
import { exportXlsx, exportPdf, exportDocx, printReport } from "@/components/Common/reportExport";

const PAGE_SIZE = 10;

const paidOn = (p) => (p.PAYMENT_DATE ? moment(p.PAYMENT_DATE).format("D MMM YYYY, hh:mm A") : "");

// Columns of the Excel / PDF / Word / print copies, in order. `width` is in
// Excel characters; the Word copy shares it out proportionally. `nowrap`
// keeps a column on one line in the PDF.
const EXPORT_COLUMNS = [
  { header: "#", width: 5, align: "center", get: (p, i) => i + 1 },
  { header: "Paid on", width: 22, nowrap: true, get: paidOn },
  { header: "Party name", width: 26, get: (p) => p.PARTY_NAME },
  { header: "Application no.", width: 15, get: (p) => p.APPLICATION_NO },
  { header: "PAN", width: 13, get: (p) => p.PAN_NO },
  { header: "Amount", width: 15, money: true, total: true, get: (p) => Number(p.CHALLAN_AMOUNT) || 0 },
  { header: "GRN", width: 19, get: (p) => p.GRN },
  { header: "CIN", width: 21, get: (p) => p.CIN },
  { header: "Department ID", width: 17, get: (p) => p.DEPARTMENT_ID },
  { header: "Office code", width: 13, get: (p) => p.OFFICE_CODE },
  { header: "E-Treasury Office Code", width: 13, get: (p) => p.DIVISION_CODE },
  { header: "Head ID", width: 17, get: (p) => p.HEADID },
];

const EXPORTERS = { xlsx: exportXlsx, pdf: exportPdf, docx: exportDocx, print: printReport };

// Fees received through eTreasury. Admins see every division, a DFO only
// their own. The search and dates are applied by the server; the list
// reloads as soon as a filter changes.
export default {
  name: "CompletedPayments",
  components: { PageHeader },
  data() {
    return {
      payments: [],
      loading: false,
      search: "",
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      page: 1,
      msg: "",
      showSnackBar: false,
      searchTimer: null,
      requestNo: 0,
      today: moment().format("YYYY-MM-DD"),
      // Which copy is being made: "xlsx", "pdf", "docx" or "print"
      exporting: null,
      // Looking up eTreasury details of rows that came without them
      filling: false,
      exportButtons: [
        { kind: "xlsx", label: "Excel", icon: "mdi-file-excel-outline", tone: "btn-act--success", title: "Download an Excel workbook (.xlsx)" },
        { kind: "pdf", label: "PDF", icon: "mdi-file-pdf-box", tone: "btn-act--danger", title: "Download a PDF" },
        { kind: "docx", label: "Word", icon: "mdi-file-word-outline", tone: "btn-act--info", title: "Download a Word document (.docx)" },
        { kind: "print", label: "Print", icon: "mdi-printer-outline", tone: "btn-act--neutral", title: "Print the list" },
      ],
    };
  },
  computed: {
    isDivision() {
      return getUserType() === "division";
    },
    subtitle() {
      return this.isDivision
        ? "Fees received through eTreasury for your division."
        : "Fees received through eTreasury across all divisions.";
    },
    hasFilters() {
      return !!((this.search && this.search.trim()) || this.fromDate || this.toDate);
    },
    total() {
      return this.payments.reduce((sum, p) => sum + (Number(p.CHALLAN_AMOUNT) || 0), 0);
    },
    periodLabel() {
      if (this.fromDate && this.toDate) return this.formatDay(this.fromDate) + " – " + this.formatDay(this.toDate);
      if (this.fromDate) return "From " + this.formatDay(this.fromDate);
      if (this.toDate) return "Up to " + this.formatDay(this.toDate);
      return "All dates";
    },
    pages() {
      return Math.ceil(this.payments.length / PAGE_SIZE);
    },
    pageRows() {
      const start = (this.page - 1) * PAGE_SIZE;
      return this.payments.slice(start, start + PAGE_SIZE);
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    load() {
      clearTimeout(this.searchTimer);
      // Only the newest request may fill the list.
      const requestNo = ++this.requestNo;
      this.loading = true;
      const text = (this.search || "").trim();
      axios
        .get("/payment/list", {
          headers: { token: localStorage.getItem("token") },
          params: {
            startDate: this.fromDate || undefined,
            endDate: this.toDate || undefined,
            search: text || undefined,
          },
        })
        .then((response) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          this.page = 1;
          if (response.data && response.data.status) {
            this.payments = response.data.data || [];
            this.fillReferences(requestNo);
          } else {
            this.payments = [];
            this.toast((response.data && response.data.msg) || "Could not load the payments.");
          }
        })
        .catch((err) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          this.payments = [];
          this.toast("Could not load the payments. Please try again.");
          console.log(err);
        });
    },
    // Payments made under the older eTreasury system come from servers
    // without the old-record lookup in /payment/list with no GRN, department
    // ID or real payment date. The application view's API has them, so they
    // are filled in from there (four at a time, remembered per application).
    fillReferences(requestNo) {
      this.filling = false;
      const missing = this.payments.filter((p) => p.APP_ID && !p.GRN && !p.DEPARTMENT_ID);
      if (!missing.length) return;
      if (!this.refCache) this.refCache = {};
      this.filling = true;
      const queue = missing.slice();
      const next = () => {
        const p = queue.shift();
        if (!p) return Promise.resolve();
        return this.referencesFor(p.APP_ID)
          .then((ref) => {
            if (ref && requestNo === this.requestNo) Object.assign(p, ref);
          })
          .catch(() => {})
          .then(next);
      };
      Promise.all([next(), next(), next(), next()]).then(() => {
        if (requestNo !== this.requestNo) return;
        this.filling = false;
        // Corrected payment dates can change the order
        this.payments = this.payments.slice().sort((a, b) => new Date(b.PAYMENT_DATE || 0) - new Date(a.PAYMENT_DATE || 0));
      });
    },
    referencesFor(appId) {
      const key = String(appId);
      if (!this.refCache[key]) {
        this.refCache[key] = axios({
          method: "POST",
          url: "/single/application",
          headers: { token: localStorage.getItem("token") },
          data: { appid: appId },
        }).then((response) => {
          const app = response.data && response.data.status && response.data.data;
          const txn = ((app && app.paymentTransactions) || []).find((t) => t.status === "Success");
          if (!txn) return null;
          const decimal = txn.AMOUNT && (txn.AMOUNT.$numberDecimal || txn.AMOUNT);
          const amount = Number(decimal) || Number(txn.totalAmount) || 0;
          const ref = {
            GRN: txn.GRN && txn.GRN !== "N/A" ? txn.GRN : "",
            CIN: txn.CIN || "",
            DEPARTMENT_ID: txn.departmentId || "",
          };
          const paidOn = txn.paymentCompleteDate || txn.paymentInitiateDate;
          if (paidOn) ref.PAYMENT_DATE = paidOn;
          if (amount) ref.CHALLAN_AMOUNT = String(amount);
          return ref;
        });
        // A failed lookup is tried again next time
        this.refCache[key].catch(() => delete this.refCache[key]);
      }
      return this.refCache[key];
    },
    // Typing waits a moment so the list reloads once, not on every key.
    searchChanged() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.load, 400);
    },
    clearSearch() {
      this.search = "";
      this.load();
    },
    clearFilters() {
      this.search = "";
      this.fromDate = null;
      this.toDate = null;
      this.load();
    },
    openApplication(p) {
      this.$router.push({ path: "/AdminFilmDetailedView", query: { id: p.APP_ID } });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDay(value) {
      return value ? moment(value).format("D MMM YYYY") : "";
    },
    formatTime(value) {
      return value ? moment(value).format("hh:mm A") : "";
    },

    // ── Copies of the current list (what the filters show) ──
    report() {
      const search = (this.search || "").trim();
      const count = this.payments.length;
      const meta = [["Scope", this.isDivision ? "Your division" : "All divisions"]];
      if (search) meta.push(["Search", search]);
      meta.push(["Generated by", localStorage.getItem("username") || "—"]);
      return {
        title: "Completed Payments",
        subtitle: this.subtitle,
        sheetName: "Payments",
        fileName: "Completed_Payments_" + moment().format("YYYY-MM-DD"),
        meta,
        summary: [
          { label: "Total collected", value: this.total, money: true },
          { label: "Payments", value: count },
          { label: "Average payment", value: count ? Math.round(this.total / count) : 0, money: true },
          { label: "Period", value: this.periodLabel },
        ],
        columns: EXPORT_COLUMNS,
        rows: this.payments,
        totalLabel: "Total · " + count + (count === 1 ? " payment" : " payments"),
      };
    },
    runExport(kind) {
      if (this.exporting || !this.payments.length) return;
      this.exporting = kind;
      EXPORTERS[kind](this.report())
        .catch((err) => {
          console.error("Export failed:", err);
          const name = { xlsx: "Excel file", pdf: "PDF", docx: "Word document", print: "print copy" }[kind];
          this.toast("Could not make the " + name + ". Please try again.");
        })
        .then(() => {
          this.exporting = null;
        });
    },
  },
};
</script>

<style scoped>
.pay-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.pay-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid var(--line);
}

.pay-stat-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.pay-stat-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--brand-dark);
  font-variant-numeric: tabular-nums;
}

.pay-stat-value--sm {
  font-size: 15px;
  padding-top: 5px;
}

.pay-amount {
  font-weight: 800;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ref-list {
  display: grid;
  gap: 2px;
  font-size: 13px;
}

.ref-key {
  display: inline-block;
  min-width: 58px;
  margin-right: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.ref-val {
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  color: #1e293b;
  word-break: break-all;
}

@media (max-width: 768px) {
  .pay-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pay-stat:last-child {
    grid-column: 1 / -1;
  }

  .ref-list {
    justify-items: end;
  }
}
</style>
